import React, { useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import CarCard from '../components/CarCard';
import { TextAdBanner, StatsBar, CarFilters, HomeReviews, TopBarTextAd, TopBarImageAd, AdRenderer } from '../components/SharedUI';
import { useSiteSettings } from '../hooks/useAppHooks';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { LuSearch, LuCarFront, LuX, LuChevronDown, LuChevronRight, LuChevronLeft, LuTag } = LuIcons;

const DEFAULT_FILTERS = { transmission: '', yearFrom: '', yearTo: '', sort: 'newest' };

const BrandDropdown = ({ cars, selectedBrand, onBrandChange }) => {
  const [open, setOpen] = useState(false);
  const availableBrands = useMemo(() => [...new Set(cars.map(c => c.brand))].sort(), [cars]);

  const handleSelect = (brand) => {
    onBrandChange(brand === selectedBrand ? '' : brand);
    setOpen(false);
  };

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('[data-brand-dropdown]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" data-brand-dropdown>
      <button onClick={() => setOpen(o => !o)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 font-bold text-sm transition-all whitespace-nowrap ${selectedBrand ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
        <SafeIcon icon={LuCarFront} className="w-4 h-4 flex-shrink-0" />
        <span className="max-w-[80px] truncate">{selectedBrand || 'الماركة'}</span>
        {selectedBrand && (
          <span role="button" onClick={e => { e.stopPropagation(); onBrandChange(''); setOpen(false); }} className="text-blue-400 hover:text-blue-700 cursor-pointer">
            <SafeIcon icon={LuX} className="w-3 h-3" />
          </span>
        )}
        <SafeIcon icon={LuChevronDown} className={`w-3 h-3 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 bg-white border border-slate-200 rounded-2xl shadow-xl z-30 min-w-[180px] overflow-hidden">
          <div className="p-2">
            <p className="text-xs font-bold text-slate-400 px-2 py-1 mb-1">اختر الماركة</p>
            <div className="max-h-52 overflow-y-auto space-y-0.5">
              {availableBrands.map(brand => (
                <button key={brand} onClick={() => handleSelect(brand)} className={`w-full text-right px-3 py-2 rounded-xl text-sm font-bold transition-all ${selectedBrand === brand ? 'bg-blue-500 text-white' : 'text-slate-700 hover:bg-slate-50'}`}>
                  {brand}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ModelDropdown = ({ cars, selectedBrand, selectedModel, onModelChange }) => {
  const [open, setOpen] = useState(false);
  const availableModels = useMemo(() => {
    if (!selectedBrand) return [];
    return [...new Set(cars.filter(c => c.brand === selectedBrand).map(c => c.model))].sort();
  }, [cars, selectedBrand]);

  React.useEffect(() => {
    onModelChange('');
  }, [selectedBrand]);

  const handleSelect = (model) => {
    onModelChange(model === selectedModel ? '' : model);
    setOpen(false);
  };

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('[data-model-dropdown]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const disabled = !selectedBrand || availableModels.length === 0;

  return (
    <div className="relative" data-model-dropdown>
      <button onClick={() => !disabled && setOpen(o => !o)} disabled={disabled} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 font-bold text-sm transition-all whitespace-nowrap ${disabled ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed' : selectedModel ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
        <span className="max-w-[80px] truncate">{selectedModel || 'الموديل'}</span>
        {selectedModel && (
          <span role="button" onClick={e => { e.stopPropagation(); onModelChange(''); setOpen(false); }} className="text-emerald-400 hover:text-emerald-700 cursor-pointer">
            <SafeIcon icon={LuX} className="w-3 h-3" />
          </span>
        )}
        {!disabled && <SafeIcon icon={LuChevronDown} className={`w-3 h-3 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />}
      </button>

      {open && (
        <div className="absolute top-full mt-2 right-0 bg-white border border-slate-200 rounded-2xl shadow-xl z-30 min-w-[160px] overflow-hidden">
          <div className="p-2">
            <div className="max-h-48 overflow-y-auto space-y-0.5">
              <button onClick={() => { onModelChange(''); setOpen(false); }} className={`w-full text-right px-3 py-2 rounded-xl text-sm font-bold transition-all ${!selectedModel ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                كل الموديلات
              </button>
              {availableModels.map(model => (
                <button key={model} onClick={() => handleSelect(model)} className={`w-full text-right px-3 py-2 rounded-xl text-sm font-bold transition-all ${selectedModel === model ? 'bg-emerald-500 text-white' : 'text-slate-700 hover:bg-slate-50'}`}>
                  {model}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const { settings } = useSiteSettings();
  const [ads, setAds] = useState([]);
  const [cars, setCars] = useState([]);
  const [loadingCars, setLoadingCars] = useState(true);
  const [carSearchTerm, setCarSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCondition, setFilterCondition] = useState('all');
  const [filterDiscount, setFilterDiscount] = useState(false);
  const [advFilters, setAdvFilters] = useState(DEFAULT_FILTERS);
  const [carBrand, setCarBrand] = useState('');
  const [carModel, setCarModel] = useState('');

  const ITEMS_PER_PAGE = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const carsGridRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [carSearchTerm, filterStatus, filterCondition, filterDiscount, advFilters, carBrand, carModel]);

  useEffect(() => {
    fetchCars();
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const { data } = await supabase.from('advertisements').select('*').eq('is_active', true);
      if (data) setAds(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCars = async () => {
    try {
      const { data } = await supabase.from('cars_for_sale').select('*')
        .order('created_at', { ascending: false });
      setCars(data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCars(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setAdvFilters(prev => ({ ...prev, [key]: value }));
  };

  const hasActiveFilters = Object.entries(advFilters).some(([k, v]) => k === 'sort' ? v !== 'newest' : v !== '');

  const discountCount = useMemo(() => cars.filter(c => c.discount_enabled && Number(c.discount_price) > 0 && c.status !== 'sold').length, [cars]);

  const filteredCars = useMemo(() => {
    let result = cars.filter(car => {
      const matchSearch = car.brand.toLowerCase().includes(carSearchTerm.toLowerCase()) || car.model.toLowerCase().includes(carSearchTerm.toLowerCase());
      const matchStatus = filterStatus === 'all' || car.status === filterStatus || (!car.status && filterStatus === 'available');
      const matchCondition = filterCondition === 'all' || car.condition === filterCondition;
      const matchTransmission = !advFilters.transmission || car.transmission === advFilters.transmission;
      const matchYearFrom = !advFilters.yearFrom || car.year >= Number(advFilters.yearFrom);
      const matchYearTo = !advFilters.yearTo || car.year <= Number(advFilters.yearTo);
      const matchBrand = !carBrand || car.brand === carBrand;
      const matchModel = !carModel || car.model === carModel;
      const matchDiscount = !filterDiscount || (car.discount_enabled && Number(car.discount_price) > 0);

      return matchSearch && matchStatus && matchCondition && matchTransmission && matchYearFrom && matchYearTo && matchBrand && matchModel && matchDiscount;
    });

    switch (advFilters.sort) {
      case 'oldest': result = [...result].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); break;
      case 'price_asc': result = [...result].sort((a, b) => Number(a.price) - Number(b.price)); break;
      case 'price_desc': result = [...result].sort((a, b) => Number(b.price) - Number(a.price)); break;
      default: break;
    }

    return result;
  }, [cars, carSearchTerm, filterStatus, filterCondition, filterDiscount, advFilters, carBrand, carModel]);

  const totalPages = Math.ceil(filteredCars.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentCars = filteredCars.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (carsGridRef.current) {
      const y = carsGridRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const availableCount = cars.filter(c => c.status !== 'sold').length;
  const soldCount = cars.filter(c => c.status === 'sold').length;
  const newCount = cars.filter(c => c.condition === 'new').length;
  const usedCount = cars.filter(c => c.condition !== 'new').length;

  return (
    <div className="flex-1 w-full flex flex-col items-center pb-10">
      {/* الإعلانات العلوية - تم إزالة الكود المكرر والاعتماد على SharedUI */}
      <TopBarTextAd ads={ads} />
      <TopBarImageAd ads={ads} />

      <div className="max-w-7xl w-full mx-auto px-3 sm:px-6 lg:px-8 mt-4">
        
        <HomeReviews location="home_above_cars" />
        
        <StatsBar />

        <section ref={carsGridRef}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0">
                <SafeIcon icon={LuCarFront} className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-3xl font-black text-slate-900">{settings.cars_title}</h2>
                <p className="text-slate-500 text-xs sm:text-sm mt-0.5">{settings.cars_subtitle}</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64">
              <div className="absolute inset-y-0 right-0 pe-3 flex items-center pointer-events-none">
                <SafeIcon icon={LuSearch} className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="البحث عن سيارة..."
                value={carSearchTerm}
                onChange={e => setCarSearchTerm(e.target.value)}
                className="block w-full pe-9 ps-4 py-2.5 border-2 border-slate-200 rounded-xl bg-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 text-sm font-bold"
              />
              {carSearchTerm && (
                <button onClick={() => setCarSearchTerm('')} className="absolute inset-y-0 left-0 ps-3 flex items-center text-slate-400 hover:text-slate-600">
                  <SafeIcon icon={LuX} className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3 items-center">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
              {[
                { val: 'all', label: `الكل (${cars.length})` },
                { val: 'available', label: `متوفرة (${availableCount})` },
                { val: 'sold', label: `مبيعة (${soldCount})` }
              ].map(f => (
                <button
                  key={f.val}
                  onClick={() => setFilterStatus(f.val)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${filterStatus === f.val ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
              {[
                { val: 'all', label: 'الكل' },
                { val: 'new', label: `جديدة (${newCount})` },
                { val: 'used', label: `مستعملة (${usedCount})` }
              ].map(f => (
                <button
                  key={f.val}
                  onClick={() => setFilterCondition(f.val)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${filterCondition === f.val ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => setFilterDiscount(d => !d)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-xs font-black transition-all ${filterDiscount ? 'border-red-400 bg-red-500 text-white shadow-md' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}
            >
              <SafeIcon icon={LuTag} className={`w-3.5 h-3.5 ${filterDiscount ? '' : 'animate-pulse'}`} />
              التخفيضات
              {!filterDiscount && discountCount > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full ms-0.5">
                  {discountCount}
                </span>
              )}
              {filterDiscount && <SafeIcon icon={LuX} className="w-3 h-3 ms-0.5" />}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mb-4 items-center">
            <BrandDropdown cars={cars} selectedBrand={carBrand} onBrandChange={(brand) => { setCarBrand(brand); setCarModel(''); }} />
            <ModelDropdown cars={cars} selectedBrand={carBrand} selectedModel={carModel} onModelChange={setCarModel} />
            <CarFilters filters={advFilters} onChange={handleFilterChange} onReset={() => setAdvFilters(DEFAULT_FILTERS)} hasActiveFilters={hasActiveFilters} />
          </div>

          {(hasActiveFilters || carBrand || carModel || filterCondition !== 'all' || filterDiscount) && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {filterDiscount && (
                <span className="flex items-center gap-1 bg-red-100 text-red-700 text-xs font-black px-2.5 py-1 rounded-full border border-red-200">
                  <SafeIcon icon={LuTag} className="w-3 h-3" /> التخفيضات فقط
                  <button onClick={() => setFilterDiscount(false)}><SafeIcon icon={LuX} className="w-3 h-3" /></button>
                </span>
              )}
              {filterCondition !== 'all' && (
                <span className="flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {filterCondition === 'new' ? 'جديدة' : 'مستعملة'}
                  <button onClick={() => setFilterCondition('all')}><SafeIcon icon={LuX} className="w-3 h-3" /></button>
                </span>
              )}
              {carBrand && (
                <span className="flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {carBrand}
                  <button onClick={() => { setCarBrand(''); setCarModel(''); }}><SafeIcon icon={LuX} className="w-3 h-3" /></button>
                </span>
              )}
              {carModel && (
                <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {carModel}
                  <button onClick={() => setCarModel('')}><SafeIcon icon={LuX} className="w-3 h-3" /></button>
                </span>
              )}
              {advFilters.transmission && (
                <span className="flex items-center gap-1 bg-violet-100 text-violet-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {advFilters.transmission === 'automatic' ? 'أوتوماتيك' : 'يدوي'}
                  <button onClick={() => handleFilterChange('transmission', '')}><SafeIcon icon={LuX} className="w-3 h-3" /></button>
                </span>
              )}
              {advFilters.sort !== 'newest' && (
                <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {{ oldest: 'الأقدم أولاً', price_asc: 'السعر: الأقل', price_desc: 'السعر: الأعلى' }[advFilters.sort]}
                  <button onClick={() => handleFilterChange('sort', 'newest')}><SafeIcon icon={LuX} className="w-3 h-3" /></button>
                </span>
              )}
            </div>
          )}

          {(hasActiveFilters || carSearchTerm || carBrand || carModel || filterCondition !== 'all' || filterDiscount) && (
            <p className="text-sm text-slate-500 mb-3 font-medium">
              تم العثور على <span className="text-slate-900 font-black">{filteredCars.length}</span> سيارة
              {filterDiscount && <span className="text-red-600 font-black"> 🏷️ مخفضة</span>}
            </p>
          )}

          <AdRenderer ads={ads} location="home_above_cars" />

          {loadingCars ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-slate-200 rounded-full w-3/4" />
                    <div className="h-3 bg-slate-100 rounded-full w-1/2" />
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-10 bg-slate-100 rounded-xl" />
                      <div className="h-10 bg-slate-100 rounded-xl" />
                    </div>
                    <div className="h-10 bg-slate-200 rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredCars.length > 0 ? (
            <>
              {filterDiscount && (
                <div className="flex items-center gap-3 mb-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-100 rounded-2xl px-4 py-3">
                  <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={LuTag} className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-black text-red-700 text-sm">عروض التخفيضات</p>
                    <p className="text-red-500 text-xs">{filteredCars.length} سيارة بأسعار مخفضة</p>
                  </div>
                </div>
              )}
              {/* إزالة خاصية layout التي كانت تسبب اختفاء الصور بسبب مشاكل حساب الأبعاد في مكتبة Framer Motion */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {currentCars.map((car, i) => (
                  <motion.div key={car.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                    <CarCard car={car} />
                  </motion.div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-1.5 mt-8 mb-4" dir="ltr">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-xl flex items-center justify-center border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors"
                  >
                    <SafeIcon icon={LuChevronLeft} className="w-4 h-4" />
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-9 h-9 rounded-xl font-bold flex items-center justify-center transition-all text-sm ${currentPage === page ? 'bg-emerald-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-xl flex items-center justify-center border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors"
                  >
                    <SafeIcon icon={LuChevronRight} className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={filterDiscount ? LuTag : LuCarFront} className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                {filterDiscount ? 'لا توجد تخفيضات حالياً' : 'لا توجد سيارات مطابقة'}
              </h3>
              <p className="text-slate-400 text-sm">
                {filterDiscount ? 'تابع الموقع للاطلاع على أحدث العروض.' : 'جرب تغيير خيارات البحث.'}
              </p>
            </motion.div>
          )}

          <HomeReviews location="home_bottom" />
          <AdRenderer ads={ads} location="home_bottom" />

        </section>
      </div>
    </div>
  );
};

export default Home;